import { useEffect, useState } from "react";
/**
 * Check if Pendo agent is fully loaded and ready
 */
const isPendoReady = () => {
    var _a;
    return Boolean((_a = window.pendo) === null || _a === void 0 ? void 0 : _a.initialize);
};
/**
 * Wait for Pendo to be ready with timeout
 */
const waitForPendo = (onReady, maxAttempts = 50, interval = 100) => {
    let attempts = 0;
    let timeoutId;
    const checkReady = () => {
        if (isPendoReady()) {
            onReady();
            return;
        }
        attempts++;
        if (attempts < maxAttempts) {
            timeoutId = setTimeout(checkReady, interval);
        }
    };
    timeoutId = setTimeout(checkReady, interval);
    return () => clearTimeout(timeoutId);
};
/**
 * Hook to dynamically load the Pendo script
 * @param apiKey - The Pendo API key (required)
 * @returns Boolean indicating if Pendo is loaded and ready
 */
export const usePendoScript = (apiKey) => {
    const [isPendoLoaded, setIsPendoLoaded] = useState(false);
    useEffect(() => {
        if (typeof window === "undefined" || !apiKey) {
            return undefined;
        }
        // Already loaded
        if (isPendoReady()) {
            setIsPendoLoaded(true);
            return undefined;
        }
        const src = `https://cdn.pendo.io/agent/static/${apiKey}/pendo.js`;
        const existingScript = document.querySelector(`script[src="${src}"]`);
        // Script already exists, wait for it to load
        if (existingScript) {
            return waitForPendo(() => setIsPendoLoaded(true));
        }
        // Create and inject new script
        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        let cleanup;
        script.onload = () => {
            cleanup = waitForPendo(() => setIsPendoLoaded(true));
        };
        script.onerror = () => {
            // eslint-disable-next-line no-console
            console.error("Failed to load Pendo script");
        };
        document.head.appendChild(script);
        return () => {
            script.remove();
            cleanup === null || cleanup === void 0 ? void 0 : cleanup();
        };
    }, [apiKey]);
    return isPendoLoaded;
};
